import { Mermaid } from "../../components/Mermaid";

const ARCHITECTURE = `flowchart LR
  iPhone["Chris's iPhone<br/>iMessage / SMS"]
  Outlook["Outlook<br/>Email"]

  subgraph MacMini["Mac Mini — isolated, Redding CA"]
    BB["BlueBubbles<br/>iMessage bridge"]
    Gateway["OpenClaw Gateway<br/>Tailscale-only"]
    SupaLocal[("Local Supabase<br/>pgvector")]
    SF["Salesforce CLI"]
  end

  subgraph VPS["VPS — Hostinger"]
    N8N["n8n<br/>workflow engine"]
    CW["Chatwoot<br/>unified inbox"]
    PG[("PostgreSQL 16<br/>+ pgvector")]
    Redis[("Redis")]
  end

  SMS["Salesforce 360<br/>SMS gateway"]

  iPhone -- "iMessage" --> BB
  iPhone -- "SMS" --> SMS
  Outlook --> N8N
  BB --> N8N
  SMS --> N8N
  N8N --> CW
  N8N -- "Tailscale only" --> Gateway
  Gateway --> SupaLocal
  Gateway --> SF
  CW --> PG
  CW --> Redis`;

const AUTO_DRAFT_FLOW = `flowchart LR
  W["Chatwoot webhook:<br/>new message"] --> F["Filter: inbound iMessage only"]
  F --> B["Build context"]
  B --> G["Get conversation history"]
  G --> FO["Find old draft"]
  FO --> DO["Delete old draft"]
  DO --> AI["Get AI draft"]
  AI --> P["Post as private note<br/>[AUTO-DRAFT]"]`;

const DISPATCH_FLOW = `flowchart TD
  W1["Webhook: SMS"] --> I1{"Outgoing SMS?"}
  W2["Webhook: iMessage"] --> I2{"Outgoing, not an echo?"}
  I1 -- yes --> N1["Normalize SMS"]
  I2 -- yes --> N2["Normalize iMessage"]
  N1 --> S["Route to Salesforce 360"]
  N2 --> BB2["Route to BlueBubbles<br/>(Tailscale)"]`;

const COPILOT_FLOW = `flowchart LR
  O["OpenAI-format webhook<br/>(Chatwoot Copilot button)"] --> R["Rewrite model name"]
  R --> C["Call OpenClaw Gateway<br/>/v1/chat/completions"]
  C --> Resp["Respond to Chatwoot"]`;

const SCHMOOZE_FLOW = `flowchart TD
  M["New inbound message"] --> D{"Needs a reply?"}
  D -- no --> SKIP["SKIP"]
  D -- yes --> S{"Sensitive?<br/>rate quote, legal, VIP crisis"}
  S -- yes --> ESC["ESCALATE<br/>straight to Chris"]
  S -- no --> DR["DRAFT reply<br/>in Chris's voice"]
  DR --> Post["Posted as private note<br/>for human approval"]`;

export function OpenClawDeepDive() {
  return (
    <div>
      <div className="pf-deepdive-head">
        <span className="eyebrow">System architecture</span>
        <h2>How the OpenClaw system actually works.</h2>
        <p>
          Two machines, one job: give a busy mortgage broker a single inbox for iMessage, SMS and
          email, with AI drafting every reply — and a hard rule that nothing sends without a human
          reading it first.
        </p>
      </div>

      <div className="pf-deepdive-block">
        <h3>High-level architecture</h3>
        <p>
          The AI gateway lives on Chris's own Mac Mini and is reachable only over Tailscale — it has
          no public port, no public DNS. The shared inbox, workflow engine and database run separately
          on a VPS. If the public-facing side were ever compromised, the AI gateway and local data
          stay isolated.
        </p>
        <Mermaid chart={ARCHITECTURE} caption="AI gateway isolated on the Mac Mini; Chatwoot + n8n + Postgres run on a separate VPS, connected only via Tailscale." />
      </div>

      <div className="pf-deepdive-block">
        <h3>Message journey</h3>
        <div className="pf-journey-steps">
          {[
            ["01", "Message arrives", "iMessage, SMS or email hits its channel."],
            ["02", "n8n normalizes it", "Creates or updates the conversation in Chatwoot."],
            ["03", "Auto-draft triggers", "Fires automatically on every inbound message."],
            ["04", "Draft posted privately", "A private note, never visible to the client."],
            ["05", "Human reviews", "Chris or his team edits and sends manually."],
            ["06", "Dispatcher routes it", "Sent back out on the right original channel."],
          ].map(([n, t, d]) => (
            <div className="pf-journey-step" key={n}>
              <span className="pf-journey-step-index">{n}</span>
              <h4>{t}</h4>
              <p>{d}</p>
            </div>
          ))}
        </div>
        <p>
          There's also an on-demand path: Chatwoot's own Copilot button calls the same AI gateway
          through a small proxy, for whenever someone wants a fresh draft outside the automatic flow.
        </p>
      </div>

      <div className="pf-deepdive-block">
        <h3>Workflow — Auto-draft on every inbound message</h3>
        <div className="pf-workflow-card">
          <div className="pf-workflow-meta">
            <span className="pf-workflow-tag">n8n workflow</span>
            <span className="pf-workflow-tag">Chatwoot Captain</span>
            <span className="pf-workflow-tag">8 nodes</span>
          </div>
          <p>
            Before posting a new draft, the workflow finds and deletes any previous draft on that
            conversation first — so a fast back-and-forth never stacks up multiple stale{" "}
            <code>[AUTO-DRAFT]</code> notes on the same thread.
          </p>
          <Mermaid chart={AUTO_DRAFT_FLOW} />
        </div>
      </div>

      <div className="pf-deepdive-block">
        <h3>Workflow — Outbound dispatcher</h3>
        <div className="pf-workflow-card">
          <div className="pf-workflow-meta">
            <span className="pf-workflow-tag">n8n workflow</span>
            <span className="pf-workflow-tag">Anti-echo logic</span>
            <span className="pf-workflow-tag">9 nodes</span>
          </div>
          <p>
            Two separate webhooks watch for outgoing SMS and outgoing iMessage. The iMessage path
            specifically checks for its own echo — a message that already has an iMessage GUID, or is
            a private note — so the dispatcher never re-sends something that was already delivered.
          </p>
          <Mermaid chart={DISPATCH_FLOW} />
        </div>
      </div>

      <div className="pf-deepdive-block">
        <h3>Workflow — Chatwoot Copilot proxy</h3>
        <div className="pf-workflow-card">
          <div className="pf-workflow-meta">
            <span className="pf-workflow-tag">n8n workflow</span>
            <span className="pf-workflow-tag">OpenAI-compatible</span>
            <span className="pf-workflow-tag">4 nodes</span>
          </div>
          <p>
            The OpenClaw gateway exposes an OpenAI-compatible <code>/v1/chat/completions</code> endpoint,
            so any tool that supports a custom OpenAI endpoint — like Chatwoot's built-in Copilot —
            connects to it with no special integration code, just a small proxy to rewrite the model name.
          </p>
          <Mermaid chart={COPILOT_FLOW} />
        </div>
      </div>

      <div className="pf-deepdive-block">
        <h3>The drafting agent</h3>
        <p>
          The drafting agent is a ghost-writer, not a chatbot — it writes as if Chris typed it himself
          and never identifies as an AI. Its voice was built from Chris's own real messages: warm but
          direct, a couple of sentences, mortgage terminology used the way a broker actually uses it.
          It makes one of three calls on every inbound message — skip it, escalate it straight to
          Chris, or draft a reply — and never quotes a rate or handles anything legally sensitive
          on its own.
        </p>
        <Mermaid chart={SCHMOOZE_FLOW} caption="Every message gets triaged before a draft is ever written." />
      </div>
    </div>
  );
}
